import { useEffect, useRef, useCallback } from 'react';
import { useCart } from '@/store/quick-cart/cart.context';
import { cartPriceUpdateService } from '@/lib/services/cart-price-update.service';

interface UseBackgroundPriceUpdatesOptions {
  intervalMs?: number; // Default: 5 minutes
  enabled?: boolean; // Default: true
  pauseWhenHidden?: boolean; // Default: true
}

/**
 * Hook for discreet background price updates
 * 
 * This hook automatically updates cart item prices in the background without
 * any visual indication to the user. It respects the cooldown period from
 * the CartPriceUpdateService to avoid unnecessary API calls.
 * 
 * Features:
 * - Runs periodically (default: every 5 minutes)
 * - Pauses when the page is hidden (tab not active)
 * - Automatically starts/stops based on cart contents
 * - Silent operation (no console logs or user notifications)
 * - Respects existing cooldown mechanisms
 * 
 * @param options Configuration options
 * @returns Object with control methods and status
 */

export const useBackgroundPriceUpdates = (options: UseBackgroundPriceUpdatesOptions = {}) => {
  const {
    intervalMs = 5 * 60 * 1000, // 5 minutes
    enabled = true,
    pauseWhenHidden = true,
  } = options;

  const { items, updateItemPrices } = useCart();
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const isVisibleRef = useRef(true);

  const updatePrices = useCallback(async () => {
    if (items.length === 0) return;

    try {
      // Exclude PBM items and pass full cart item information
      const nonPBMItems = items.filter((item: any) => !item?.pbm_flag);
      const cartItems = nonPBMItems.map(item => ({
        sku: item.sku || item.id,
        id: item.id,
        price: item.price,
        sale_price: item.sale_price,
        promotional_pack_unit_price: item.promotional_pack_unit_price,
        promotional_pack_quantity: item.promotional_pack_quantity,
        product_prices: item.product_prices,
        quantity: item.quantity,
        stock: item.stock,
      }));

      const result = await cartPriceUpdateService.updatePrices(cartItems, {
        silent: true, // No console logs
        forceUpdate: false, // Respect cooldown
      });

      if (result.success && result.updatedItems.length > 0) {
        // Update prices in cart without any visual indication
        updateItemPrices(result.updatedItems);
      }
    } catch (error) {
      // Silently handle errors - no user notification
      console.error('Background price update failed:', error);
    }
  }, [items, updateItemPrices]);

  const startInterval = useCallback(() => {
    if (!enabled || items.length === 0) return;

    // Clear existing interval
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    // Start new interval
    intervalRef.current = setInterval(() => {
      if (isVisibleRef.current || !pauseWhenHidden) {
        updatePrices();
      }
    }, intervalMs);
  }, [enabled, items.length, intervalMs, updatePrices, pauseWhenHidden]);

  const stopInterval = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  // Handle visibility change
  useEffect(() => {
    if (!pauseWhenHidden) return;

    const handleVisibilityChange = () => {
      isVisibleRef.current = !document.hidden;
      
      if (isVisibleRef.current && enabled && items.length > 0) {
        // Resume updates when page becomes visible
        startInterval();
      } else if (!isVisibleRef.current) {
        // Pause updates when page is hidden
        stopInterval();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [enabled, items.length, pauseWhenHidden, startInterval, stopInterval]);

  // Start/stop interval based on cart items and enabled state
  useEffect(() => {
    if (enabled && items.length > 0) {
      startInterval();
    } else {
      stopInterval();
    }

    return () => {
      stopInterval();
    };
  }, [enabled, items.length, startInterval, stopInterval]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopInterval();
    };
  }, [stopInterval]);

  return {
    startInterval,
    stopInterval,
    updatePrices,
    isRunning: intervalRef.current !== null,
  };
};
